import { useEffect, useState } from "react";
import { WorkoutLogPanel } from "../free-weights/WorkoutLogPanel";
import { track } from "../../analytics";
import type { PRHit } from "../../api/types";
import { TOKENS } from '../ui/dashboard-primitives'
import { Dialog } from '../ui/Dialog'

/**
 * WorkoutLogModal — manual workout logging, lifted OUT of the coach chat window
 * into its own focused overlay.
 *
 * Opened by the coach's `workout_log` follow-up pill (`AICoach.tsx`), which
 * dispatches the `coach:open-log` window event this listens for. That pill is
 * the only trigger left: the "Log it →" button in the home briefing strip used
 * to dispatch the same event, and Spec D deleted the strip.
 *
 * On save it emits `coach:log-saved` with any PR exercise names so the coach
 * chat can celebrate them conversationally — the two surfaces stay decoupled
 * and the chat window is no longer a container for the log form.
 *
 * On the shared `Dialog` (Apple design review X-8, SH-3): portalled to <body>,
 * so the nav no longer paints above its scrim (it sat in <main>'s stacking
 * context), focus goes in and back to the pill that opened it, Tab stays
 * inside, the page behind is inert, and the exit plays. The scrim's 3 px
 * blur went with it, DESIGN.md bans `backdrop-filter` (X-5), and the scrim
 * rose from 0.5 to 0.62 so the page behind stays as quiet without it.
 */

const WARM_DARK = "rgba(24, 20, 18, 0.96)";
const CREAM_FAINT = TOKENS.creamBorder;

export function WorkoutLogModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      // Analytics (#708): the log opening is the web's "workout started" (it has no live workout). No parameters.
      track("workout_start");
    };
    window.addEventListener("coach:open-log", handler);
    return () => window.removeEventListener("coach:open-log", handler);
  }, []);

  const handleSave = (prs: PRHit[]) => {
    setOpen(false);
    // Records only. A progress note is a real signal but not a personal
    // record, and announcing one as a PR is exactly the distinction the two
    // tiers exist to make.
    const prNames = prs
      .filter((p) => p.metric === "weight_kg" && p.tier === "record")
      .map((p) => p.exercise_name);
    if (prNames.length > 0) {
      window.dispatchEvent(new CustomEvent("coach:log-saved", { detail: { prNames } }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      label="Log workout"
      align="top"
      scrim="rgba(14, 11, 10, 0.62)"
      className="mt-4 w-full max-w-[560px] overflow-hidden rounded-2xl sm:mt-12"
      style={{
        background: WARM_DARK,
        border: `1px solid ${CREAM_FAINT}`,
        boxShadow: "0 32px 80px -28px rgba(20, 14, 12, 0.7)",
      }}
    >
      <WorkoutLogPanel show onClose={() => setOpen(false)} onSave={handleSave} />
    </Dialog>
  );
}
